import { useEffect, useState } from 'react'
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { ExploreView } from './components/ExploreView'
import { FeesPanel } from './components/FeesPanel'
import { LaunchPanel } from './components/LaunchPanel'
import { SolanaWalletProvider } from './components/WalletProvider'
import { SITE_URL, TOKEN } from './lib/constants'
import './App.css'

type Tab = 'explore' | 'create' | 'fees'

function tabFromHash(): Tab {
  const h = window.location.hash.replace('#', '').toLowerCase()
  if (h === 'fees' || h === 'claim') return 'fees'
  if (h === 'explore') return 'explore'
  if (h === 'create' || h === 'launch') return 'create'
  return 'create'
}

function SiteFooter({ onNavigate }: { onNavigate: (t: Tab) => void }) {
  return (
    <footer className="site-footer">
      <div className="footer-card">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="brand-mark">
              ex<em>fund</em>
            </div>
            <p>
              Launch Solana tokens from this site with pump.fun as the on-chain
              backend. Fees that help people move on — moving deposits, rent
              bridges, counseling co-pays. Your wallet submits every
              transaction. EXFUND does not custody assets.
            </p>
          </div>
          <div className="footer-col">
            <h4>Product</h4>
            <ul>
              <li>
                <button type="button" className="linkish" onClick={() => onNavigate('explore')}>
                  Explore
                </button>
              </li>
              <li>
                <button type="button" className="linkish" onClick={() => onNavigate('create')}>
                  Create
                </button>
              </li>
              <li>
                <button type="button" className="linkish" onClick={() => onNavigate('fees')}>
                  Fees
                </button>
              </li>
              <li>
                <a href={SITE_URL} target="_blank" rel="noreferrer">
                  Site
                </a>
              </li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <ul>
              <li>
                <span style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>
                  Not financial advice
                </span>
              </li>
              <li>
                <span style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>
                  Not a registered charity
                </span>
              </li>
            </ul>
          </div>
          <div className="footer-col footer-risk">
            <h4>Risk notice</h4>
            <p>
              Meme coins are high risk and can go to zero. Transactions are
              submitted via your wallet. Tokens can be volatile. No guaranteed
              returns. On-chain reality is what your wallet and the public
              ledger show.
            </p>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © {new Date().getFullYear()} EXFUND ·{' '}
            {SITE_URL.replace(/^https?:\/\//, '')}
          </span>
          <span>Solana · @pump-fun/pump-sdk</span>
        </div>
      </div>
    </footer>
  )
}

function AppShell() {
  const [tab, setTab] = useState<Tab>(() =>
    typeof window !== 'undefined' ? tabFromHash() : 'create',
  )

  useEffect(() => {
    const onHash = () => setTab(tabFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const go = (t: Tab) => {
    setTab(t)
    const hash = t === 'create' ? 'create' : t
    if (window.location.hash !== `#${hash}`) {
      window.location.hash = hash
    }
  }

  return (
    <div className="page">
      <header className="site-header">
        <div className="site-header-inner">
          <a
            className="brand"
            href="#explore"
            onClick={(e) => {
              e.preventDefault()
              go('explore')
            }}
          >
            <img src={TOKEN.logoPath} alt="" width={34} height={34} />
            <span className="brand-mark">
              ex<em>fund</em>
            </span>
          </a>

          <nav className="nav-tabs" aria-label="Primary">
            <button
              type="button"
              className={tab === 'explore' ? 'active' : ''}
              onClick={() => go('explore')}
            >
              Explore
            </button>
            <button
              type="button"
              className={tab === 'create' ? 'active' : ''}
              onClick={() => go('create')}
            >
              Create
            </button>
            <button
              type="button"
              className={tab === 'fees' ? 'active' : ''}
              onClick={() => go('fees')}
            >
              Fees
            </button>
          </nav>

          <div className="header-actions">
            <WalletMultiButton />
          </div>
        </div>
      </header>

      <div className="page-inner" id="top">
        {tab === 'explore' && <ExploreView onCreate={() => go('create')} />}
        {tab === 'create' && <LaunchPanel onBack={() => go('explore')} />}
        {tab === 'fees' && <FeesPanel />}
      </div>

      <SiteFooter onNavigate={go} />
    </div>
  )
}

export default function App() {
  return (
    <SolanaWalletProvider>
      <AppShell />
    </SolanaWalletProvider>
  )
}
